import React, { useState } from 'react';
import Seo from '../components/Seo';
import { site, breadcrumbSchema } from '../seo';

const MacroCalculator = () => {
  const [formData, setFormData] = useState({
    age: '',
    gender: 'male',
    weight: '',
    height: '',
    activityLevel: '1.2',
    goal: 'maintain'
  });

  const [results, setResults] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateMacros = (e) => {
    e.preventDefault();
    
    const { age, gender, weight, height, activityLevel, goal } = formData;
    
    // Convert inputs to numbers
    const ageNum = parseInt(age);
    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height);
    const activityNum = parseFloat(activityLevel);

    // Calculate BMR using Mifflin-St Jeor Equation
    let bmr;
    if (gender === 'male') {
      bmr = (10 * weightNum) + (6.25 * heightNum) - (5 * ageNum) + 5;
    } else {
      bmr = (10 * weightNum) + (6.25 * heightNum) - (5 * ageNum) - 161;
    }

    // Calculate TDEE
    const tdee = bmr * activityNum;

    // Adjust calories based on goal
    let targetCalories;
    switch (goal) {
      case 'lose':
        targetCalories = tdee - 500; // 500 calorie deficit
        break;
      case 'gain':
        targetCalories = tdee + 500; // 500 calorie surplus
        break;
      default:
        targetCalories = tdee;
    }

    // Calculate macros (40% carbs, 30% protein, 30% fat)
    const protein = Math.round((targetCalories * 0.30) / 4); // 4 calories per gram
    const carbs = Math.round((targetCalories * 0.40) / 4); // 4 calories per gram
    const fat = Math.round((targetCalories * 0.30) / 9); // 9 calories per gram

    setResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      targetCalories: Math.round(targetCalories),
      protein,
      carbs,
      fat
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <Seo
        title={site.routes['/macro-calculator'].title}
        description={site.routes['/macro-calculator'].description}
        schema={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Macro Calculator', path: '/macro-calculator' }
        ])}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Macro Calculator</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Calculate your daily macronutrient needs based on your goals, activity level, and body composition.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={calculateMacros} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2">
                  Age (years)
                </label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your age"
                />
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div>
                <label htmlFor="weight" className="block text-sm font-medium text-gray-700 mb-2">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  required
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your weight in kg"
                />
              </div>

              <div>
                <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-2">
                  Height (cm)
                </label>
                <input
                  type="number"
                  id="height"
                  name="height"
                  value={formData.height}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your height in cm"
                />
              </div>

              <div>
                <label htmlFor="activityLevel" className="block text-sm font-medium text-gray-700 mb-2">
                  Activity Level
                </label>
                <select
                  id="activityLevel"
                  name="activityLevel"
                  value={formData.activityLevel}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1.2">Sedentary (little/no exercise)</option>
                  <option value="1.375">Lightly active (light exercise 1-3 days/week)</option>
                  <option value="1.55">Moderately active (moderate exercise 3-5 days/week)</option>
                  <option value="1.725">Very active (hard exercise 6-7 days/week)</option>
                  <option value="1.9">Extremely active (very hard exercise, physical job)</option>
                </select>
              </div>

              <div>
                <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
                  Goal
                </label>
                <select
                  id="goal"
                  name="goal"
                  value={formData.goal}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="lose">Lose Weight</option>
                  <option value="maintain">Maintain Weight</option>
                  <option value="gain">Gain Weight</option>
                </select>
              </div>
            </div>

            <div className="text-center">
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105"
              >
                Calculate Macros
              </button>
            </div>
          </form>

          {results && (
            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Your Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg text-center">
                  <h3 className="text-lg font-semibold text-gray-700">BMR</h3>
                  <p className="text-2xl font-bold text-blue-600">{results.bmr}</p>
                  <p className="text-sm text-gray-500">calories/day</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg text-center">
                  <h3 className="text-lg font-semibold text-gray-700">TDEE</h3>
                  <p className="text-2xl font-bold text-blue-600">{results.tdee}</p>
                  <p className="text-sm text-gray-500">calories/day</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg text-center">
                  <h3 className="text-lg font-semibold text-gray-700">Target Calories</h3>
                  <p className="text-2xl font-bold text-green-600">{results.targetCalories}</p>
                  <p className="text-sm text-gray-500">calories/day</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg text-center">
                  <h3 className="text-lg font-semibold text-gray-700">Goal</h3>
                  <p className="text-lg font-bold text-gray-700 capitalize">{formData.goal} Weight</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-6 rounded-lg text-center">
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Protein</h3>
                  <p className="text-3xl font-bold text-red-600">{results.protein}g</p>
                  <p className="text-sm text-gray-500 mt-1">30% of calories</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg text-center">
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Carbohydrates</h3>
                  <p className="text-3xl font-bold text-yellow-600">{results.carbs}g</p>
                  <p className="text-sm text-gray-500 mt-1">40% of calories</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg text-center">
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Fat</h3>
                  <p className="text-3xl font-bold text-purple-600">{results.fat}g</p>
                  <p className="text-sm text-gray-500 mt-1">30% of calories</p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-yellow-100 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Important Notes:</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• BMR = Basal Metabolic Rate (calories burned at rest)</li>
                  <li>• TDEE = Total Daily Energy Expenditure (BMR + activity)</li>
                  <li>• These are estimates - individual needs may vary</li>
                  <li>• Consult with a healthcare professional for personalized advice</li>
                  <li>• Adjust portions based on your progress and how you feel</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Understanding Macronutrients</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-red-600">P</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Protein</h3>
              <p className="text-gray-600 text-sm">
                Essential for muscle repair, growth, and maintenance. Found in meat, fish, eggs, dairy, legumes, and nuts.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-yellow-600">C</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Carbohydrates</h3>
              <p className="text-gray-600 text-sm">
                Primary energy source for your body and brain. Found in grains, fruits, vegetables, and dairy products.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">F</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Fat</h3>
              <p className="text-gray-600 text-sm">
                Important for hormone production, nutrient absorption, and energy storage. Found in oils, nuts, seeds, and fatty fish.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacroCalculator;
